import React from "react";
import { useShallow } from "zustand/react/shallow";
import { useAuth } from "context/AuthContext";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import { labelAmbienteSii } from "utils/siiAmbiente";
import {
  MdOutlineScience,
  MdOutlineVerified,
  MdOutlineErrorOutline,
  MdOutlineAddBusiness,
} from "react-icons/md";

/**
 * Muestra el ambiente SII (certificación / producción) según los perfiles de emisión DTE.
 * Estado compartido vía Zustand (misma carga que el sidebar).
 */
export default function NavbarAmbienteBadge() {
  const { user } = useAuth();
  const state = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      profileUuid: s.profileUuid,
      profiles: s.profiles,
    })),
  );

  if (!user) return null;

  const shellBase =
    "group flex max-w-full min-w-0 items-center gap-2.5 border px-2.5 py-1.5 transition-[box-shadow,transform] duration-200 sm:gap-3 sm:px-3 sm:py-2";

  if (state.kind === "idle" || state.kind === "loading") {
    return (
      <div
        className={`${shellBase} rounded-2xl border-slate-200/90 bg-white/90 shadow-sm hover:shadow-md dark:border-white/10 dark:bg-navy-900/80`}
        aria-busy={state.kind === "loading"}
        aria-live="polite"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-white/10">
          <span className="h-4 w-4 animate-pulse rounded bg-slate-300 dark:bg-white/20" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
            Emisor DTE
          </p>
          <p className="truncate text-sm font-semibold text-slate-500 dark:text-slate-300">
            {state.kind === "loading" ? "Cargando perfil…" : "—"}
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div
        className={`${shellBase} rounded-2xl border-red-200 bg-red-50/95 shadow-sm dark:border-red-500/35 dark:bg-red-950/35`}
        title="No se pudo cargar el perfil de emisión"
        role="status"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-300">
          <MdOutlineErrorOutline className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-red-600/80 dark:text-red-300/90">
            Emisor DTE
          </p>
          <p className="truncate text-sm font-semibold text-red-800 dark:text-red-100">
            No se pudo cargar
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === "empty") {
    return (
      <div
        className={`${shellBase} rounded-2xl border-slate-200 bg-slate-50/90 shadow-sm dark:border-white/10 dark:bg-navy-900/80`}
        title="Configurá un perfil de emisión en Administración"
        role="status"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-200/90 text-slate-500 dark:bg-white/10 dark:text-slate-400">
          <MdOutlineAddBusiness className="h-5 w-5 opacity-90" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
            Emisor DTE
          </p>
          <p className="truncate text-sm font-semibold text-slate-600 dark:text-slate-300">
            Sin perfil configurado
          </p>
        </div>
      </div>
    );
  }

  const { ambiente, profileUuid, profiles } = state;
  const selectedProfile = profiles.find((p) => p.profileUuid === profileUuid) || null;
  const currentStatus =
    String(selectedProfile?.status || "active").trim().toLowerCase() === "active"
      ? "Activo"
      : "Inactivo";
  const ambienteLabel = labelAmbienteSii(ambiente) || "—";

  const variant =
    !ambiente
      ? {
          shell: `${shellBase} rounded-2xl shadow-sm hover:shadow-md`,
          wrap: "border-slate-200 bg-gradient-to-br from-slate-50 to-white dark:border-white/10 dark:from-navy-900 dark:to-navy-900/90",
          iconBg: "bg-slate-200/80 text-slate-600 dark:bg-white/10 dark:text-slate-300",
          Icon: MdOutlineScience,
          labelMuted: "text-slate-400 dark:text-slate-500",
          titleColor: "text-slate-800 dark:text-slate-100",
          iconWrap: "rounded-xl",
        }
      : ambiente === "produccion"
        ? {
            shell: `${shellBase} rounded-lg shadow-lg shadow-emerald-950/25 hover:shadow-xl hover:shadow-emerald-950/35`,
            wrap: "border-2 border-emerald-700/90 bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-950 text-white dark:border-emerald-500/80 dark:from-emerald-900 dark:via-emerald-800 dark:to-emerald-950",
            rootStyle: {
              background:
                "linear-gradient(135deg, rgb(6 95 70) 0%, rgb(4 120 87) 55%, rgb(6 78 59) 100%)",
              borderColor: "rgb(6 95 70 / 0.9)",
            },
            iconBg:
              "bg-white/20 text-white ring-1 ring-white/40 dark:ring-emerald-200/25",
            Icon: MdOutlineVerified,
            labelMuted: "text-emerald-100/95",
            titleColor: "text-white drop-shadow-sm",
            iconWrap: "rounded-lg",
          }
        : {
            shell: `${shellBase} rounded-2xl shadow-sm hover:shadow-md`,
            wrap: "border-amber-200/90 bg-gradient-to-br from-amber-50/95 to-white dark:border-amber-500/30 dark:from-amber-950/45 dark:to-navy-900/90",
            iconBg:
              "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
            Icon: MdOutlineScience,
            labelMuted: "text-amber-800/80 dark:text-amber-200/90",
            titleColor: "text-amber-950 dark:text-amber-50",
            iconWrap: "rounded-xl",
          };

  const { shell, wrap, iconBg, Icon, labelMuted, titleColor, iconWrap, rootStyle } =
    variant;

  return (
    <div
      className={`${shell} ${wrap}`}
      style={rootStyle}
      title={ambienteLabel}
      role="status"
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center ${iconWrap} ${iconBg}`}
        aria-hidden
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={`text-[11px] font-medium uppercase tracking-[0.12em] ${labelMuted}`}
        >
          {ambiente === "produccion" ? "En vivo · SII" : "Ambiente SII"}
        </p>
        <p
          className={`truncate text-sm font-semibold leading-snug ${titleColor}`}
        >
          {`${ambienteLabel} · ${currentStatus}`}
        </p>
      </div>
    </div>
  );
}
